import { EQuestionType } from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { buildAnswerOptions } from 'components/Rules/helpers/answerOptions';
import {
  applyOperatorChange,
  operatorsForQuestionType,
  replaceCondition,
  supportsAnswerCondition,
} from 'components/Rules/helpers/ruleUpdates';
import RuleConditionIfAnswerShell from 'components/Rules/RuleConditionIfAnswer';
import RuleConditionIfAnswerForm from 'components/Rules/RuleConditionIfAnswerForm';
import RuleConditionIfAnswerSelects from 'components/Rules/RuleConditionIfAnswerSelects';
import { upsertQuestionRule } from 'rtk/Question.Editor.Slice';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestion,
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionOptions,
  selectQuestionType,
} from 'rtk/questionEditorSelector';
import type { TQuestionRule, TQuestionRuleCondition } from 'types/rules.types';

interface Props {
  rule: TQuestionRule;
  condition: TQuestionRuleCondition;
  conditionIdx: number;
}

const RuleConditionIfAnswer = ({ rule, condition, conditionIdx }: Props) => {
  const { t } = useTranslation('Templates');
  const { questionId } = useQuestionEditorContext();
  const dispatch = useAppDispatch();
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const type = useAppSelector((state) => selectQuestionType(state, questionId));
  const options = useAppSelector((state) => selectQuestionOptions(state, questionId));
  const language = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const question = useAppSelector((state) => selectQuestion(state, questionId));

  const commitRule = (nextRule: TQuestionRule) => dispatch(upsertQuestionRule({ questionId, rule: nextRule }));
  const commit = (patch: Partial<TQuestionRuleCondition>) => commitRule(replaceCondition(rule, conditionIdx, patch));

  return (
    <RuleConditionIfAnswerShell
      condition={condition}
      isReadOnly={isReadOnly}
      allowedOperators={operatorsForQuestionType(type)}
      onOperatorChange={(operator) => commitRule(applyOperatorChange(rule, conditionIdx, operator, type))}
    >
      {type === EQuestionType.SingleSelect || type === EQuestionType.MultipleSelect ? (
        <RuleConditionIfAnswerSelects
          condition={condition}
          isReadOnly={isReadOnly}
          items={buildAnswerOptions(options, language ? [language] : [])}
          onChange={commit}
        />
      ) : null}
      {type === EQuestionType.Form ? (
        <RuleConditionIfAnswerForm
          condition={condition}
          isReadOnly={isReadOnly}
          language={question?.language}
          forms={question?.forms}
          onChange={commit}
        />
      ) : null}
      {supportsAnswerCondition(type) ? null : (
        <div className="text-danger-800">{t('question.ruleConditions.answerConditionUnsupportedType')}</div>
      )}
    </RuleConditionIfAnswerShell>
  );
};

export default RuleConditionIfAnswer;
